import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { SiteMap } from '@/components/sites/SiteMap';
import { AddressAutocomplete } from '@/components/sites/AddressAutocomplete';
import { SiteMissingCoordinates } from '@/utils/excelImport';
import { MapPin, Building2, SkipForward } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Site } from '@/types/site';

interface SiteCoordinatesReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sitesWithoutCoordinates: SiteMissingCoordinates[];
  onComplete: () => void;
}

interface SiteCoordinateData {
  street: string;
  postal_code: string;
  city: string;
  country: string;
  lat?: number;
  lon?: number;
  skip: boolean;
}

export function SiteCoordinatesReviewDialog({
  open,
  onOpenChange,
  sitesWithoutCoordinates,
  onComplete,
}: SiteCoordinatesReviewDialogProps) {
  const [siteData, setSiteData] = useState<Map<string, SiteCoordinateData>>(new Map());
  const [showMapPicker, setShowMapPicker] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize state for each site
  useEffect(() => {
    const initial = new Map<string, SiteCoordinateData>();
    sitesWithoutCoordinates.forEach(site => {
      initial.set(site.id, {
        street: '',
        postal_code: '',
        city: '',
        country: '',
        lat: undefined,
        lon: undefined,
        skip: false,
      });
    });
    setSiteData(initial);
  }, [sitesWithoutCoordinates]);

  const updateSiteData = (siteId: string, updates: Partial<SiteCoordinateData>) => {
    setSiteData(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(siteId) || {
        street: '',
        postal_code: '',
        city: '',
        country: '',
        skip: false,
      };
      newMap.set(siteId, { ...current, ...updates });
      return newMap;
    });
  };

  const handleAddressSelect = (siteId: string, address: {
    street: string;
    postal_code: string;
    city: string;
    country: string;
    lat: number;
    lon: number;
  }) => {
    updateSiteData(siteId, {
      street: address.street,
      postal_code: address.postal_code,
      city: address.city,
      country: address.country,
      lat: address.lat,
      lon: address.lon,
    });
  };

  const handleMapPick = (siteId: string, coords: { lat: number; lon: number }) => {
    updateSiteData(siteId, {
      lat: coords.lat,
      lon: coords.lon,
    });
  };

  const handleSkipAll = () => {
    onComplete();
    onOpenChange(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let updateCount = 0;

      for (const site of sitesWithoutCoordinates) {
        const data = siteData.get(site.id);
        if (!data || data.skip) continue;

        // Only update if we have coordinates
        if (data.lat !== undefined && data.lon !== undefined) {
          const addressParts = [data.street, data.postal_code, data.city].filter(Boolean);
          const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : undefined;

          const metadata: any = {
            coordinates: {
              lat: data.lat,
              lon: data.lon,
            },
          };

          if (data.street || data.postal_code || data.city) {
            metadata.structured_address = {
              street: data.street || '',
              postal_code: data.postal_code || '',
              city: data.city || '',
            };
          }

          await supabase
            .from('sites')
            .update({
              address: fullAddress,
              country: data.country || undefined,
              metadata,
            })
            .eq('id', site.id);
          
          updateCount++;
        }
      }

      if (updateCount > 0) {
        toast.success(`Updated coordinates for ${updateCount} site(s)`);
      }

      onComplete();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving coordinates:', error);
      toast.error('Failed to save some coordinates');
    } finally {
      setIsSaving(false);
    }
  };

  const sitesWithCoords = sitesWithoutCoordinates.filter(site => {
    const data = siteData.get(site.id);
    return data && !data.skip && data.lat !== undefined && data.lon !== undefined;
  });

  // Create site object for map picker
  const getMapSite = (siteId: string): Site => {
    const site = sitesWithoutCoordinates.find(s => s.id === siteId);
    const data = siteData.get(siteId);
    return {
      id: siteId,
      org_id: '',
      name: site?.name || '',
      created_at: '',
      updated_at: '',
      metadata: data?.lat && data?.lon ? {
        coordinates: { lat: data.lat, lon: data.lon }
      } : undefined,
    };
  };

  return (
    <>
      <Dialog open={open && !showMapPicker} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Set Site Coordinates
            </DialogTitle>
            <DialogDescription>
              {sitesWithoutCoordinates.length} site(s) were imported without GPS coordinates. 
              You can set their location now or skip this step.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[55vh] pr-4">
            <div className="space-y-6">
              {sitesWithoutCoordinates.map(site => {
                const data = siteData.get(site.id);
                const hasCoords = data?.lat !== undefined && data?.lon !== undefined;

                return (
                  <div 
                    key={site.id} 
                    className={`rounded-lg border p-4 space-y-4 ${data?.skip ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{site.name}</span>
                        {site.address && (
                          <Badge variant="outline" className="text-xs">
                            {site.address}
                          </Badge>
                        )}
                        {hasCoords && (
                          <Badge variant="secondary" className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            ✓ Coordinates set
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`skip-${site.id}`}
                          checked={data?.skip || false}
                          onCheckedChange={(checked) => 
                            updateSiteData(site.id, { skip: checked === true })
                          }
                        />
                        <Label htmlFor={`skip-${site.id}`} className="text-sm text-muted-foreground cursor-pointer">
                          Skip
                        </Label>
                      </div>
                    </div>

                    {!data?.skip && (
                      <>
                        <div className="space-y-2">
                          <Label>Search Address</Label>
                          <AddressAutocomplete
                            value={data?.street || ''}
                            onChange={(value) => updateSiteData(site.id, { street: value })}
                            onSelect={(address) => handleAddressSelect(site.id, address)}
                            placeholder="Start typing to search for an address..."
                          />
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-2">
                            <Label>Postal Code</Label>
                            <Input
                              value={data?.postal_code || ''}
                              onChange={(e) => updateSiteData(site.id, { postal_code: e.target.value })}
                              placeholder="e.g., 153 91"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>City</Label>
                            <Input
                              value={data?.city || ''}
                              onChange={(e) => updateSiteData(site.id, { city: e.target.value })}
                              placeholder="e.g., Stockholm"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Country Code</Label>
                            <Input
                              value={data?.country || ''}
                              onChange={(e) => updateSiteData(site.id, { country: e.target.value.toUpperCase() })}
                              placeholder="e.g., SE"
                              maxLength={2}
                            />
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="flex-1 grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label>Latitude</Label>
                              <Input
                                type="number"
                                step="any"
                                value={data?.lat ?? ''}
                                onChange={(e) => updateSiteData(site.id, { 
                                  lat: e.target.value ? parseFloat(e.target.value) : undefined 
                                })}
                                placeholder="e.g., 59.3293"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Longitude</Label>
                              <Input
                                type="number"
                                step="any"
                                value={data?.lon ?? ''}
                                onChange={(e) => updateSiteData(site.id, { 
                                  lon: e.target.value ? parseFloat(e.target.value) : undefined 
                                })}
                                placeholder="e.g., 18.0686"
                              />
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowMapPicker(site.id)}
                            className="mt-6"
                          >
                            <MapPin className="h-4 w-4 mr-2" />
                            Pick on Map
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          <DialogFooter className="flex justify-between sm:justify-between">
            <Button variant="ghost" onClick={handleSkipAll} disabled={isSaving}>
              <SkipForward className="h-4 w-4 mr-2" />
              Skip All
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : sitesWithCoords.length > 0 
                  ? `Save ${sitesWithCoords.length} Coordinate(s)` 
                  : 'Continue'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Map Picker Modal */}
      {showMapPicker && (
        <Dialog open={!!showMapPicker} onOpenChange={() => setShowMapPicker(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] z-[100]">
            <DialogHeader>
              <DialogTitle>Pick Location on Map</DialogTitle>
              <DialogDescription>
                Drag the marker to select the exact location. The coordinates will update as you move the marker.
              </DialogDescription>
            </DialogHeader>
            <div className="h-[500px] rounded-lg overflow-hidden">
              <SiteMap
                sites={[getMapSite(showMapPicker)]}
                onMapClick={(coords) => handleMapPick(showMapPicker, coords)}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMapPicker(null)}>
                Cancel
              </Button>
              <Button onClick={() => setShowMapPicker(null)}>
                Confirm Location
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
